import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Client, Project } from '../../types';
import { invoicesAPI, clientsAPI, projectsAPI } from '../../services/api';
import { CURRENCIES, NAVIGATION, UNITS, INVOICE_STATUSES } from '../../constants';
import FileUpload from '../../components/files/FileUpload';
import { generateAndUploadInvoicePDF } from '../../utils/invoiceUtils';
import { useFiles } from '../../hooks/useFiles';
import { formatNumber } from '../../utils/formatNumber';
import { getMonthOptions, getCurrentMonthValue } from '../../constants/months';

const schema = z.object({
  invoiceNumber: z.string().min(1, 'Invoice number is required'),
  clientId: z.string().min(1, 'Client is required'),
  projectId: z.string().optional(),
  issueDate: z.string().optional(),
  dueDate: z.string().min(1, 'Due date is required'),
  currency: z.string().min(1, 'Currency is required'),
  notes: z.string().optional(),
  status: z.string().optional(),
  items: z.array(z.object({
    description: z.string().min(1, 'Description is required'),
    unitPrice: z.string().min(1, 'Price is required'),
    quantity: z.string().min(1, 'Quantity is required'),
    unit: z.string(),
    amount: z.number().optional()
  })).min(1, 'At least one item is required'),
  paidDate: z.string().optional(),
  rsdAmount: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const EditInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  
  // Month filter state (not stored in database)
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthValue);
  const { files, uploadFile, deleteFile } = useFiles('INVOICE', id!);

  const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue, control, reset } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      invoiceNumber: '',
      clientId: '',
      projectId: '',
      issueDate: '',
      dueDate: '',
      currency: CURRENCIES.EUR,
      notes: '',
      status: INVOICE_STATUSES.DRAFT,
      items: [
        {
          description: '',
          unitPrice: '',
          quantity: '',
          unit: UNITS.HOUR,
          amount: 0
        }
      ],
      paidDate: '',
      rsdAmount: '',
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedValues = watch();
  const hasChanges = originalData && (
    watchedValues.invoiceNumber !== originalData.invoiceNumber ||
    watchedValues.clientId !== originalData.clientId ||
    watchedValues.projectId !== originalData.projectId ||
    watchedValues.issueDate !== originalData.issueDate ||
    watchedValues.dueDate !== originalData.dueDate ||
    watchedValues.currency !== originalData.currency ||
    watchedValues.notes !== originalData.notes ||
    watchedValues.status !== originalData.status ||
    watchedValues.paidDate !== originalData.paidDate ||
    JSON.stringify(watchedValues.items) !== JSON.stringify(originalData.items)
  );

  // Fetch data
  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesAPI.getById(id!)
  });

  const { data: clients } = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: clientsAPI.getAll
  });

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: projectsAPI.getAll
  });

  const watchedItems = watch('items');
  const watchedCurrency = watch('currency');
  const watchedClientId = watch('clientId');

  // Filter projects by selected client
  useEffect(() => {
    if (watchedClientId && projects) {
      const clientProjects = projects.filter(project => project.clientId === watchedClientId);
      setFilteredProjects(clientProjects);
      // Reset project selection if current project is not for selected client
      const currentProjectId = watch('projectId');
      if (currentProjectId && !clientProjects.find(p => p.id === currentProjectId)) {
        setValue('projectId', '');
      }
    } else {
      setFilteredProjects([]);
      setValue('projectId', '');
    }
  }, [watchedClientId, projects, setValue, watch]);

  // Calculate amounts when price or quantity changes
  useEffect(() => {
    if (watchedItems) {
      watchedItems.forEach((item, index) => {
        const price = parseFloat(item.unitPrice) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const amount = Math.round((price * quantity) * 100) / 100; // Round to 2 decimal places
        setValue(`items.${index}.amount`, amount);
      });
    }
  }, [watchedItems, setValue]);

  useEffect(() => {
    if (invoice) {
      const formData = {
        invoiceNumber: invoice.invoiceNumber || '',
        clientId: invoice.client?.id || '',
        projectId: invoice.project?.id || '',
        issueDate: invoice.issueDate ? invoice.issueDate.substring(0, 10) : '',
        dueDate: invoice.dueDate ? invoice.dueDate.substring(0, 10) : '',
        currency: invoice.currency || CURRENCIES.EUR,
        notes: invoice.notes || '',
        status: invoice.status || INVOICE_STATUSES.DRAFT,
        rsdAmount: invoice.rsdAmount ? invoice.rsdAmount.toString() : '',
        items: invoice.items?.length > 0 
          ? invoice.items.map((item: any) => ({
              description: item.description || '',
              unitPrice: item.unitPrice?.toString() || '',
              quantity: item.quantity?.toString() || '',
              unit: item.unit || UNITS.HOUR,
              amount: item.amount || 0,
            }))
          : [{ description: '', unitPrice: '', quantity: '', unit: UNITS.HOUR, amount: 0 }],
        paidDate: invoice.paidDate ? invoice.paidDate.substring(0, 10) : '',
      };
      reset(formData);
      setOriginalData(formData);
    }
  }, [invoice, reset]);

  const addItem = () => {
    append({
      description: '',
      unitPrice: '',
      quantity: '',
      unit: UNITS.HOUR,
      amount: 0
    });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const calculateSubtotal = () => {
    if (!watchedItems) return 0;
    return watchedItems.reduce((total, item) => {
      const amount = item.amount || 0;
      return total + amount;
    }, 0);
  };

  const calculatePDV = () => {
    const selectedClient = clients?.find(client => client.id === watchedClientId);
    const isDomestic = selectedClient?.isDomestic || false;
    return isDomestic ? calculateSubtotal() * 0.2 : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculatePDV();
  };

  const onSubmit = async (data: FormData) => {
    setSuccess(''); 
    setError('');
    try {
      const payload: any = {
        invoiceNumber: data.invoiceNumber,
        clientId: data.clientId,
        dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : undefined,
        currency: data.currency,
        items: data.items.map(item => ({
          description: item.description,
          unitPrice: parseFloat(item.unitPrice),
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          amount: parseFloat(item.unitPrice) * parseFloat(item.quantity)
        }))
      };
      
      if (data.projectId) payload.projectId = data.projectId;
      if (data.issueDate && data.issueDate.trim()) payload.issueDate = new Date(data.issueDate).toISOString();
      if (data.notes) payload.notes = data.notes;
      if (data.status) payload.status = data.status;
      if (data.rsdAmount && data.rsdAmount.trim()) payload.rsdAmount = parseFloat(data.rsdAmount);
      
      // Handle paid date - send null if cleared, otherwise send the date
      if (data.paidDate && data.paidDate.trim()) {
        payload.paidDate = new Date(data.paidDate).toISOString();
      } else {
        payload.paidDate = null;
      }
      
      await invoicesAPI.update(id!, payload);
      
      // Show dialog to ask if user wants to generate PDF
      setPendingFormData(data);
      setShowPdfDialog(true);
      
      setSuccess('Invoice updated successfully!');
    } catch (err: any) {
      console.error('Update error:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to update invoice');
    }
  };

  const handlePdfGeneration = async (generatePdf: boolean) => {
    setShowPdfDialog(false);
    
    if (generatePdf && pendingFormData) {
      try {
        // Generate PDF and upload it as a file
        await generateAndUploadInvoicePDF(id!, pendingFormData.invoiceNumber);
        setSuccess('Invoice updated successfully and PDF generated!');
      } catch (err: any) {
        console.error('PDF generation error:', err);
        setError('Invoice updated but PDF generation failed');
      }
    } else {
      setSuccess('Invoice updated successfully!');
    }
    
    setTimeout(() => navigate(NAVIGATION.INVOICES), 1000);
  };

  if (isLoading || !originalData || !clients || !projects) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
        <Typography variant="h5" fontWeight={700} mb={2}>Edit Invoice</Typography>
        <Typography>Loading invoice data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Edit Invoice</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <TextField 
              label="Invoice Number" 
              fullWidth 
              {...register('invoiceNumber')} 
              error={!!errors.invoiceNumber} 
              helperText={errors.invoiceNumber?.message}
              value={watch('invoiceNumber') || ''}
            />
            <TextField 
              label="Client" 
              select 
              fullWidth 
              {...register('clientId')} 
              error={!!errors.clientId} 
              helperText={errors.clientId?.message}
              value={watch('clientId') || ''}
            >
              {clients.map(client => (
                <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>
              ))}
            </TextField>
            <TextField 
              label="Project" 
              select 
              fullWidth 
              {...register('projectId')} 
              error={!!errors.projectId} 
              helperText={errors.projectId?.message}
              value={filteredProjects.find(p => p.id === watch('projectId')) ? watch('projectId') || '' : ''}
              disabled={!watchedClientId}
            >
              <MenuItem value="">None</MenuItem>
              {filteredProjects.length > 0 ? (
                filteredProjects.map(project => (
                  <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {watchedClientId ? 'No projects available for this client' : 'Select a client first'}
                </MenuItem>
              )}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <TextField 
              label="Currency" 
              select 
              fullWidth 
              {...register('currency')} 
              error={!!errors.currency} 
              helperText={errors.currency?.message}
              value={watch('currency') || CURRENCIES.EUR}
            >
              <MenuItem value={CURRENCIES.EUR}>{CURRENCIES.EUR} (€)</MenuItem>
              <MenuItem value={CURRENCIES.USD}>{CURRENCIES.USD} ($)</MenuItem>
              <MenuItem value={CURRENCIES.GBP}>{CURRENCIES.GBP} (£)</MenuItem>
              <MenuItem value={CURRENCIES.RSD}>{CURRENCIES.RSD} (din)</MenuItem>
              <MenuItem value={CURRENCIES.CHF}>{CURRENCIES.CHF} (CHF)</MenuItem>
            </TextField>
            <TextField
              label="Status" 
              select 
              fullWidth 
              {...register('status')} 
              error={!!errors.status} 
              helperText={errors.status?.message}
              value={watch('status') || INVOICE_STATUSES.DRAFT}
            >
              <MenuItem value={INVOICE_STATUSES.DRAFT}>{INVOICE_STATUSES.DRAFT}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.SENT}>{INVOICE_STATUSES.SENT}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.PAID}>{INVOICE_STATUSES.PAID}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.OVERDUE}>{INVOICE_STATUSES.OVERDUE}</MenuItem>
              <MenuItem value={INVOICE_STATUSES.UNPAID}>{INVOICE_STATUSES.UNPAID}</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
            <TextField 
              label="Issue Date" 
              type="date" 
              fullWidth 
              {...register('issueDate')} 
              InputLabelProps={{ shrink: true }} 
              error={!!errors.issueDate} 
              helperText={errors.issueDate?.message} 
            />
            
            <TextField 
              label="Due Date" 
              type="date" 
              fullWidth 
              {...register('dueDate')} 
              InputLabelProps={{ shrink: true }} 
              error={!!errors.dueDate} 
              helperText={errors.dueDate?.message} 
            />

            <TextField 
                label="Paid Date" 
                type="date" 
                fullWidth 
                {...register('paidDate')} 
                InputLabelProps={{ shrink: true }} 
                error={!!errors.paidDate} 
                helperText={errors.paidDate?.message} 
            />
          </Box>

          <TextField 
            label="Notes" 
            fullWidth 
            multiline 
            rows={2}
            {...register('notes')} 
            error={!!errors.notes} 
            helperText={errors.notes?.message} 
          />

           <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, my: 2 }}>
             <TextField 
               label="Month Filter" 
               select 
               fullWidth 
               value={selectedMonth}
               onChange={(e) => setSelectedMonth(e.target.value)}
               helperText="Filter timesheet data by month"
             >
               {getMonthOptions().map(option => (
                 <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
               ))}
             </TextField>
             <TextField 
               label="RSD Amount" 
               type="number"
               fullWidth 
               {...register('rsdAmount')}
               error={!!errors.rsdAmount} 
               helperText={errors.rsdAmount?.message || "Enter the invoice amount in RSD for reporting purposes"}
               InputLabelProps={{ shrink: true }}
             />
           </Box>
        </Box>

        <Box sx={{ my: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Invoice Items</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addItem}
              variant="outlined"
              size="small"
            >
              Add Item
            </Button>
          </Box>

          {fields.map((field, index) => (
            <Box key={field.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Item {index + 1}</Typography>
                {fields.length > 1 && (
                  <IconButton 
                    onClick={() => removeItem(index)} 
                    color="error" 
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
                <TextField 
                  label="Description" 
                  fullWidth 
                  {...register(`items.${index}.description`)}
                  error={!!errors.items?.[index]?.description}
                  helperText={errors.items?.[index]?.description?.message}
                />
                
                <TextField 
                  label="Unit" 
                  select 
                  fullWidth 
                  {...register(`items.${index}.unit`)}
                  value={watch(`items.${index}.unit`) || UNITS.HOUR}
                >
                  <MenuItem value={UNITS.HOUR}>{UNITS.HOUR}</MenuItem>
                  <MenuItem value={UNITS.DAY}>{UNITS.DAY}</MenuItem>
                  <MenuItem value={UNITS.MONTH}>{UNITS.MONTH}</MenuItem>
                  <MenuItem value={UNITS.PIECE}>{UNITS.PIECE}</MenuItem>
                  <MenuItem value={UNITS.SERVICE}>{UNITS.SERVICE}</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <TextField 
                  label="Price" 
                  type="number" 
                  fullWidth 
                  {...register(`items.${index}.unitPrice`)}
                  error={!!errors.items?.[index]?.unitPrice}
                  helperText={errors.items?.[index]?.unitPrice?.message}
                  onChange={(e) => {
                    const price = parseFloat(e.target.value) || 0;
                    const quantity = parseFloat(watch(`items.${index}.quantity`)) || 0;
                    const amount = Math.round((price * quantity) * 100) / 100; // Round to 2 decimal places
                    setValue(`items.${index}.amount`, amount);
                  }}
                />
                
                <TextField 
                  label="Quantity" 
                  type="number" 
                  fullWidth 
                  {...register(`items.${index}.quantity`)}
                  error={!!errors.items?.[index]?.quantity}
                  helperText={errors.items?.[index]?.quantity?.message}
                  onChange={(e) => {
                    const quantity = parseFloat(e.target.value) || 0;
                    const price = parseFloat(watch(`items.${index}.unitPrice`)) || 0;
                    const amount = Math.round((price * quantity) * 100) / 100; // Round to 2 decimal places
                    setValue(`items.${index}.amount`, amount);
                  }}
                />
                
                <TextField 
                  label="Amount" 
                  type="number" 
                  fullWidth 
                  value={watch(`items.${index}.amount`) || 0}
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { bgcolor: 'action.hover' } }}
                />
              </Box>
            </Box>
          ))}

          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">{formatNumber(calculateSubtotal())} {watchedCurrency}</Typography>
            </Box>
            {calculatePDV() > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">PDV (20%):</Typography>
                <Typography variant="body1">{formatNumber(calculatePDV())} {watchedCurrency}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">{formatNumber(calculateTotal())} {watchedCurrency}</Typography>
            </Box>
          </Box>
        </Box>

        {/* File Upload Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Files</Typography>
          <FileUpload 
            entityType="INVOICE"
            entityId={id!}
            files={files}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            onFilesChange={() => {}} // Files are managed by useFiles hook
          />
        </Box>

        <Button 
          type="submit" 
          variant="contained" 
          color="primary" 
          fullWidth 
          sx={{ mt: 2 }} 
          disabled={isSubmitting || !hasChanges}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>

      {/* PDF Generation Dialog */}
      <Dialog open={showPdfDialog} onClose={() => setShowPdfDialog(false)}>
        <DialogTitle>Generate PDF?</DialogTitle>
        <DialogContent>
          <Typography>
            Would you like to generate a new PDF for this invoice? 
            This will create an updated PDF file with your changes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handlePdfGeneration(false)} color="primary">
            Skip PDF
          </Button>
          <Button onClick={() => handlePdfGeneration(true)} variant="contained" color="primary">
            Generate PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditInvoice; 